import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api";
import NavBar from "../components/NavBar";
import ReferralDetailsModal from "../components/ReferralDetailsModal";
import GenderBadge from "../components/GenderBadge";
import { formatDate } from "../utils/dateUtils";
import { useLoading } from "../contexts/LoadingContext";
import {
  statusStyleMap,
  clientStatusOptions as statusOptions,
} from "../utils/statusColors";

const ClientDashboard = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const handleStatusChange = async (referralId, newStatus) => {
    if (!newStatus) return;
    try {
      setGlobalLoadingMessage("Updating status...");
      setGlobalLoading(true);
      await apiFetch(`/referrals/client/${referralId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setReferrals((prev) =>
        prev.map((r) => (r._id === referralId ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      console.error("Failed to update status:", err);
      setError(err.message);
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      setGlobalLoadingMessage("Loading referrals...");
      setGlobalLoading(true);
      const data = await apiFetch("/referrals/client");
      const list = data.data || [];
      setReferrals(list);
      // Keep the modal's referral in sync with the freshly fetched list so
      // its prop-driven UI (status pill, etc.) reflects the latest data.
      setSelectedReferral((prev) =>
        prev ? list.find((r) => r._id === prev._id) || prev : prev
      );
    } catch (err) {
      console.error("Failed to fetch referrals:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const openDetailsModal = (referral) => {
    setSelectedReferral(referral);
    setDetailsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-[var(--color-text)]">Loading your referrals...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <NavBar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--color-text-strong)]">
            My Referrals
          </h1>
          <p className="text-[var(--color-text-muted)] mt-1">
            View profiles referred to you by your marriage bureau
          </p>
        </div>

        {/* Referrals Grid */}
        {referrals.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[var(--color-text-muted)] text-lg mb-2">
              No referrals yet
            </div>
            <div className="text-[var(--color-text-muted)]">
              Your bureau will send you profile referrals soon
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {referrals.map((referral) => (
              <div
                key={referral._id}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                {/* Status Dropdown (top-left) */}
                <div className="flex justify-between items-start mb-4">
                  {(() => {
                    const triggerStyle = statusStyleMap[referral.status] || { bg: "#f3f4f6", fg: "#1f2937" };
                    const arrow = encodeURIComponent(triggerStyle.fg);
                    return (
                      <select
                        value={statusOptions.some((o) => o.value === referral.status) ? referral.status : ""}
                        onChange={(e) => handleStatusChange(referral._id, e.target.value)}
                        className="appearance-none px-3 py-1 pr-7 text-xs font-medium rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-primary)]"
                        style={{
                          backgroundColor: triggerStyle.bg,
                          color: triggerStyle.fg,
                          backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='${arrow}'%3E%3Cpath d='M5.25 7.5L10 12.25 14.75 7.5z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 0.5rem center",
                          backgroundSize: "0.65rem",
                        }}
                      >
                        <option value="" disabled style={{ backgroundColor: "#ffffff", color: "#6b7280" }}>
                          Select Status
                        </option>
                        {statusOptions.map((opt) => {
                          const s = statusStyleMap[opt.value] || { bg: "#ffffff", fg: "#1f2937" };
                          return (
                            <option
                              key={opt.value}
                              value={opt.value}
                              style={{ backgroundColor: s.bg, color: s.fg }}
                            >
                              {opt.label}
                            </option>
                          );
                        })}
                      </select>
                    );
                  })()}
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDate(referral.createdAt)}
                  </span>
                </div>

                {/* Profile Info */}
                <div className="mb-4">
                  <h3 className="flex items-center gap-2 font-semibold text-[var(--color-text-strong)] text-lg mb-2">
                    <span>{referral.profile?.fullName || "Unknown"}</span>
                    <GenderBadge gender={referral.profile?.gender} />
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Reference ID:</span>
                      <span className="text-[var(--color-text)]">{referral.profile?.referenceId || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Age:</span>
                      <span className="text-[var(--color-text)]">{referral.profile?.age || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">City:</span>
                      <span className="text-[var(--color-text)]">{referral.profile?.city || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Profession:</span>
                      <span className="text-[var(--color-text)]">{referral.profile?.profession || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Marital Status:</span>
                      <span className="text-[var(--color-text)]">{referral.profile?.maritalStatus || "N/A"}</span>
                    </div>
                  </div>
                </div>

                {/* Bureau Info */}
                <div className="bg-[var(--color-surface-muted)] p-3 rounded-lg mb-4">
                  <div className="text-sm text-[var(--color-text-muted)]">Referred by:</div>
                  <div className="font-medium text-[var(--color-text-strong)]">
                    {referral.bureau?.name || "Your Bureau"}
                  </div>
                </div>

                {/* Bureau Notes */}
                {referral.bureauNotes && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-[var(--color-text-strong)] mb-1">
                      Bureau Notes:
                    </div>
                    <div className="text-sm text-[var(--color-text-muted)] bg-[var(--color-surface-muted)] p-2 rounded">
                      {referral.bureauNotes}
                    </div>
                  </div>
                )}

                {/* Client Comments */}
                {referral.clientComments && referral.clientComments.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-[var(--color-text-strong)] mb-2">
                      Your Comments:
                    </div>
                    <div className="space-y-2">
                      {referral.clientComments.map((comment, index) => (
                        <div key={index} className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-sm">
                          <div className="text-[var(--color-text)]">{comment.comment}</div>
                          {comment.rating && (
                            <div className="text-xs text-[var(--color-text-muted)] mt-1">
                              Rating: {comment.rating}/5
                            </div>
                          )}
                          <div className="text-xs text-[var(--color-text-muted)] mt-1">
                            {formatDate(comment.createdAt)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="grid gap-3">
                  <button
                    onClick={() => openDetailsModal(referral)}
                    className="w-full px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailsModalOpen && selectedReferral && (
        <ReferralDetailsModal
          referral={selectedReferral}
          isOpen={detailsModalOpen}
          isClient
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedReferral(null);
          }}
          onUpdate={fetchReferrals}
        />
      )}
    </div>
  );
};

export default ClientDashboard;